import { css } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'

import { Flex } from '../flex'
import { Touchable } from '../touchable'

import ArrowRight from './arrow-right.svg'

import range from '../../utils/range'

const baseStyle = {
  padding: '5px',
  border: '0',
  display: 'inline-flex',
  backgroundColor: 'transparent',
  justifyContent: 'center',
  alignItems: 'center',
  width: '35px',
  height: '35px',
  cursor: 'pointer',
  color: '#A7AEB3',
  fontWeight: '600',
  borderRadius: '20px',
  whiteSpace: 'nowrap',
  ':hover': {
    backgroundColor: '#f5f5f5',
  },
  ':focus': {
    backgroundColor: '#fafafa',
  },
  '.intent-mouse &:focus': {
    outline: 'none',
  },
}

const dotsStyle = {
  padding: '10px 5px',
}
const disabledStyle = {
  opacity: '0.5',
  cursor: 'normal',
}
const selectedStyle = {
  color: '#000',
}

interface IPageNumberProps {
  pageNumber?: number | null
  selected: boolean
  onClick: () => void
}

class PageNumber extends React.Component<IPageNumberProps> {
  static propTypes = {
    pageNumber: PropTypes.number,
    selected: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
  }

  render() {
    return this.props.pageNumber == null ? (
      <div data-test-class="dots" {...css([baseStyle, disabledStyle, dotsStyle])}>
        {'…'}
      </div>
    ) : this.props.selected ? (
      <Flex style={[baseStyle, this.props.selected && selectedStyle]}>{this.props.pageNumber + 1}</Flex>
    ) : (
      <Touchable onClick={this.props.onClick} style={baseStyle}>
        {this.props.pageNumber + 1}
      </Touchable>
    )
  }
}

interface IPageArrowProps {
  direction: 'back' | 'forward'
  onClick: () => void
  disabled: boolean
}

class PageArrow extends React.Component<IPageArrowProps> {
  static propTypes = {
    direction: PropTypes.oneOf(['back', 'forward']),
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool.isRequired,
  }

  render() {
    const { direction, disabled, onClick } = this.props
    const arrow = (
      <ArrowRight
        {...css(
          direction === 'back'
            ? { transform: 'rotate(180deg)', width: '20px', height: '20px' }
            : { width: '20px', height: '20px' }
        )}
      />
    )

    return disabled ? (
      <Flex style={[baseStyle, disabledStyle]}>{arrow}</Flex>
    ) : (
      <Touchable onClick={onClick} style={baseStyle}>
        {arrow}
      </Touchable>
    )
  }
}

interface IPaginationPageNumbersProps {
  page?: number
  pageSize?: number
  totalCount?: number
  onPageChange: (page: number) => void
}

export class PaginationPageNumbers extends React.Component<IPaginationPageNumbersProps> {
  static propTypes = {
    page: PropTypes.number,
    pageSize: PropTypes.number.isRequired,
    totalCount: PropTypes.number,
    onPageChange: PropTypes.func.isRequired,
  }

  render() {
    const { totalCount, pageSize, page, onPageChange } = this.props
    let numbers = []
    const pages = totalCount && pageSize && Math.ceil(totalCount / pageSize)

    if (pages == null) {
      return null
    }

    if (!totalCount || pageSize == null || page == null || pages <= 1) {
      return null
    } else if (pages <= 7) {
      numbers = range(pages)
    } else if (page <= 3) {
      numbers = [...range(5), null, pages - 1]
    } else if (pages - page >= 5) {
      numbers = [0, null, page - 1, page, page + 1, null, pages - 1]
    } else {
      numbers = [0, null, pages - 5, pages - 4, pages - 3, pages - 2, pages - 1]
    }

    return (
      <Flex row>
        <PageArrow direction="back" disabled={page === 0} onClick={() => onPageChange(page - 1)} />
        {numbers.map((i, j) => (
          <PageNumber
            key={`${i}${j}` + (page === i)}
            onClick={() => i != null && onPageChange(i)}
            pageNumber={i}
            selected={page === i}
          />
        ))}
        <PageArrow
          direction="forward"
          disabled={(page + 1) * pageSize >= totalCount}
          onClick={() => onPageChange(page + 1)}
        />
      </Flex>
    )
  }
}
